import { useInfiniteQuery } from "@tanstack/react-query";
import { previewDraft } from "@/api/management";
import { PANEL_PAGE_SIZE, LIST_QUERY_OPTIONS } from "@/lib/config-pagination";
import { createContext, useContext } from "react";

import type { DraftOperation, PreviewResponse } from "@/api/management";
import type { DraftNode, DraftState } from "@/lib/management-draft";
import type { ScopeVerdict } from "@/lib/org-structure-tree";

export interface ManagementDraftValue extends DraftState {
  /** Identifica a sequência de operações cuja prévia precisa estar atualizada. */
  previewRevision: string;
  details?: {
    projectId: string;
    artifactId?: string;
    operations: DraftOperation[];
  };
  previewCurrent: boolean;
  /** Operações no rascunho, mesmo as que se anulam (`changes` conta só o efeito líquido). */
  pendingOperations: number;
  stage: (operation: DraftOperation) => void;
  /** Prévia do rascunho com mais uma operação (para confirmar a cascata antes). */
  previewWith: (operation: DraftOperation) => Promise<PreviewResponse>;
  discard: () => void;
  /** Grava; devolve se deu certo (o rascunho fica na tela se falhar). */
  save: () => Promise<boolean>;
  saving: boolean;
}

export const ManagementDraftContext =
  createContext<ManagementDraftValue | null>(null);
const EMPTY: ReadonlyMap<string, ScopeVerdict> = new Map();

export const MANAGEMENT_SAVE_KEY = (projectId: string) => [
  "config-server",
  "scope-verdicts",
  projectId,
];

/** Dentro do Gerenciamento: o rascunho. Fora dele (execução), null. */
export function useManagementDraft(): ManagementDraftValue | null {
  return useContext(ManagementDraftContext);
}

/** Decisões do rascunho de um nó; vazio fora do Gerenciamento. */
export function useDraftVerdicts(
  node: DraftNode,
): ReadonlyMap<string, ScopeVerdict> {
  return useContext(ManagementDraftContext)?.verdicts[node] ?? EMPTY;
}

/** Detalhes por painel; a identidade de ocultação continua no mapa completo `codes`. */
export function useDraftItems(node: DraftNode, verdict: ScopeVerdict) {
  const draft = useManagementDraft();
  const details = draft?.details;
  const query = useInfiniteQuery({
    queryKey: [
      "config-server",
      "stages",
      details?.projectId ?? "",
      "management",
      "preview-details",
      details?.artifactId,
      details?.operations,
      node,
      verdict,
    ],
    queryFn: ({ pageParam }) =>
      previewDraft(
        details!.projectId,
        details!.operations,
        details!.artifactId,
        {
          details_node: node,
          details_verdict: verdict,
          cursor: pageParam,
          limit: PANEL_PAGE_SIZE,
        },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursors[node] ?? undefined,
    enabled: !!details?.operations.length && !!draft?.previewCurrent,
    ...LIST_QUERY_OPTIONS,
  });
  return {
    items:
      query.data?.pages.flatMap((page) => page.items) ??
      (draft?.items ?? []).filter(
        (item) => item.node === node && item.verdict === verdict,
      ),
    hasMore: !!query.hasNextPage,
    loading: query.isFetching,
    loadMore: () => void query.fetchNextPage(),
    error: query.isError,
    retry: () => void query.refetch(),
  };
}
